import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  IAllStaffAttendanceRow,
  IDoctorAttendanceRow,
  IGroomerAttendanceSummary,
} from "@/api_framework/api_modals/Configuration";
import { useClinicDailyAttendanceAPI } from "@/app/hooks/api_hooks/configuration/useClinicDailyAttendanceAPI";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import AlertActionModal, {
  ALERT_LABELS,
  ALL_STAFF_REASON_LABELS,
  GROOMER_REASON_LABELS,
  REASON_LABELS,
} from "./AlertActionModal";

const DATE_FORMAT = "yyyy-MM-dd";

const ALERT_STYLES: Record<string, string> = {
  STAFF_ABSENT: "bg-rose-50 text-rose-700 ring-rose-200",
  SHORT_STAFF: "bg-rose-50 text-rose-700 ring-rose-200",
  SHORT_SHIFT: "bg-amber-50 text-amber-800 ring-amber-300",
  MORE_STAFF: "bg-sky-50 text-sky-700 ring-sky-200",
  EXTRA_STAFF: "bg-sky-50 text-sky-700 ring-sky-200",
  ROTA_NOT_PRESENT: "bg-sky-50 text-sky-700 ring-sky-200",
  NO_OUT_TIME: "bg-slate-100 text-slate-600 ring-slate-300",
};

const ALERT_DOTS: Record<string, string> = {
  STAFF_ABSENT: "bg-rose-500",
  SHORT_STAFF: "bg-rose-500",
  SHORT_SHIFT: "bg-amber-500",
  MORE_STAFF: "bg-sky-500",
  EXTRA_STAFF: "bg-sky-500",
  ROTA_NOT_PRESENT: "bg-sky-500",
  NO_OUT_TIME: "bg-slate-400",
};

const AlertChip = ({ code }: { code: string }) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${ALERT_STYLES[code]}`}
  >
    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ALERT_DOTS[code]}`} />
    {ALERT_LABELS[code]}
  </span>
);

const STAFF_TYPE_LABELS: Record<string, string> = {
  doctor: "Doctor",
  paravet: "Paravet",
  groomer: "Groomer",
  helper: "Helper",
  clinic_manager: "Clinic Manager",
  assistant_clinic_manager: "Assistant Clinic Manager",
  housekeeping: "Housekeeping",
  area_manager: "Area Manager",
  vet_assistant: "Vet Assistant",
  clinic_staff: "Clinic Staff",
  pharmacist: "Pharmacist",
};

const formatDuration = (minutes: number | null) => {
  if (minutes === null || minutes === undefined) return "—";
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
};

const emptyCell = <span className="text-[#6C727F]">—</span>;

const ACTIONED_STYLE =
  "inline-flex max-w-[240px] items-start gap-1 text-[11px] leading-4 text-[#6C727F]";

const ActionButton = ({
  label,
  onClick,
  disabled,
  tone = "default",
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "danger";
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`rounded-md px-1 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15 ${
      disabled
        ? "cursor-default text-[#6C727F]"
        : tone === "danger"
          ? "text-rose-600 hover:underline"
          : "text-[#193A6A] hover:underline"
    }`}
  >
    {label}
  </button>
);

const EmptyNote = ({ message, description }: { message: string; description?: string }) => (
  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center">
    <p className="text-xs font-medium text-slate-700">{message}</p>
    {description && <p className="mt-1 text-[11px] leading-4 text-[#6C727F]">{description}</p>}
  </div>
);

const Section = ({
  heading,
  description,
  children,
}: {
  heading: string;
  description: string;
  children: React.ReactNode;
}) => (
  <div className="mt-6 first:mt-0">
    <h2 className="text-sm font-semibold text-slate-900">{heading}</h2>
    <p className="mt-0.5 text-xs text-[#6C727F]">{description}</p>
    <div className="mt-3">{children}</div>
  </div>
);

const StaffAttendanceClosingComponent = () => {
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const { execute: logAPI } = useClickLogAPI();

  const [doctorActionRow, setDoctorActionRow] = useState<IDoctorAttendanceRow | null>(null);
  const [allStaffActionRow, setAllStaffActionRow] = useState<IAllStaffAttendanceRow | null>(null);
  const [groomerActionAlert, setGroomerActionAlert] = useState<string | null>(null);

  const date = format(new Date(), DATE_FORMAT);

  const { execute: fetchAttendance, doctors, groomerSummary, allStaff, isLoading, errors } =
    useClinicDailyAttendanceAPI();

  useEffect(() => {
    if (!mappedClinicId) return;
    fetchAttendance(mappedClinicId, date);
  }, [mappedClinicId, date, fetchAttendance]);

  useEffect(() => {
    if (!mappedClinicId) return;
    logAPI({
      event_type: "STAFF_ATTENDANCE_SCREEN_OPENED",
      event_action: "SUCCESS",
      scr_id: "day_closing_staff_attendance",
      timestamp: Date.now(),
      vms_user_id: userLogInfo,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      attendance_date: date,
    });
  }, [mappedClinicId]);

  const groomerColumns = [
    { header: "Total Groomers as per ROTA", cell: (row: IGroomerAttendanceSummary) => row.rota_staff },
    {
      header: "Total shift time as per ROTA",
      cell: (row: IGroomerAttendanceSummary) => (
        <span className="text-slate-700">{formatDuration(row.rota_minutes)}</span>
      ),
    },
    {
      header: "Attended Groomers",
      cell: (row: IGroomerAttendanceSummary) => {
        const attended = row.staff.filter((member) => member.in_time);
        if (!attended.length) return emptyCell;
        return (
          <div className="flex flex-col items-start gap-0.5">
            {attended.map((member) => (
              <span key={member.employee_code} className="text-slate-700">
                {member.name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      header: "Attended time",
      cell: (row: IGroomerAttendanceSummary) => (
        <span className="text-slate-700">{formatDuration(row.attended_minutes)}</span>
      ),
    },
    {
      header: "Alert",
      cell: (row: IGroomerAttendanceSummary) => {
        if (!row.alerts.length) return emptyCell;
        return (
          <div className="flex flex-col items-start gap-1">
            {row.alerts.map((code) => {
              return (
                <div key={code} className="flex min-h-[32px] flex-col items-start justify-center gap-0.5">
                  <AlertChip code={code} />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      header: "Action",
      cell: (row: IGroomerAttendanceSummary) => {
        if (!row.alerts.length) return emptyCell;
        return (
          <div className="flex flex-col items-start gap-1">
            {row.alerts.map((code) => {
              const action = row.actions[code];
              const recorded = action ? GROOMER_REASON_LABELS[code][action.reason_code] : null;
              return (
                <div key={code} className="flex min-h-[32px] flex-col items-start justify-center">
                  {action ? (
                    <>
                      <span className={ACTIONED_STYLE} title={recorded}>
                        <span className="line-clamp-2">{recorded}</span>
                      </span>
                      {action.reason_label && (
                        <span className={ACTIONED_STYLE} title={action.reason_label}>
                          <span>{action?.action_note}</span>
                        </span>
                      )}
                    </>
                  ) : (
                    <ActionButton label="Take Action" tone="danger" onClick={() => setGroomerActionAlert(code)} />
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  const doctorColumns = [
    { header: "SN", cell: (_: any, index: number) => index + 1 },
    { header: "Name", accessor: "name" },
    { header: "In Time", cell: (row: IDoctorAttendanceRow) => row.in_time || emptyCell },
    { header: "Out Time", cell: (row: IDoctorAttendanceRow) => row.out_time || emptyCell },
    {
      header: "ROTA Shift",
      cell: (row: IDoctorAttendanceRow) => row.rota_shift || <span className="text-[#6C727F]">Not rostered</span>,
    },
    {
      header: "Attended",
      cell: (row: IDoctorAttendanceRow) => (
        <span className="text-slate-700">{formatDuration(row.attended_minutes)}</span>
      ),
    },
    {
      header: "Alert",
      cell: (row: IDoctorAttendanceRow) => {
        if (!row.alert) return emptyCell;
        return <AlertChip code={row.alert} />;
      },
    },
    {
      header: "Action",
      cell: (row: IDoctorAttendanceRow) => {
        if (!row.alert || !REASON_LABELS[row.alert]) return emptyCell;
        if (row.action) {
          const recorded = REASON_LABELS[row.alert][row.action.reason_code];
          return (
            <div className="flex flex-col items-start gap-0.5">
              <span className={ACTIONED_STYLE} title={recorded}>
                <span className="line-clamp-2">{recorded}</span>
              </span>
              {row.action.reason_label && (
                <span className={ACTIONED_STYLE} title={row.action.reason_label}>
                  <span>{row.action?.action_note}</span>
                </span>
              )}
            </div>
          );
        }
        return <ActionButton label="Take Action" tone="danger" onClick={() => setDoctorActionRow(row)} />;
      },
    },
  ];

  const allStaffColumns = [
    { header: "SN", cell: (_: any, index: number) => index + 1 },
    { header: "Name", accessor: "name" },
    { header: "Staff Type", cell: (row: IAllStaffAttendanceRow) => STAFF_TYPE_LABELS[row.staff_type] },
    { header: "Sub Type", cell: (row: IAllStaffAttendanceRow) => row.sub_type || emptyCell },
    {
      header: "Status",
      cell: (row: IAllStaffAttendanceRow) => (
        <span className={row.is_present ? "text-emerald-700" : "text-rose-600"}>
          {row.is_present ? "Present" : "Absent"}
        </span>
      ),
    },
    { header: "In Time", cell: (row: IAllStaffAttendanceRow) => row.in_time || emptyCell },
    { header: "Out Time", cell: (row: IAllStaffAttendanceRow) => row.out_time || emptyCell },
    {
      header: "Attended",
      cell: (row: IAllStaffAttendanceRow) => (
        <span className="text-slate-700">{formatDuration(row.attended_minutes)}</span>
      ),
    },
    {
      header: "Alert",
      cell: (row: IAllStaffAttendanceRow) => {
        if (!row.alert) return emptyCell;
        return <AlertChip code={row.alert} />;
      },
    },
    {
      header: "Action",
      cell: (row: IAllStaffAttendanceRow) => {
        if (!row.alert) return emptyCell;
        if (row.action) {
          const recorded = ALL_STAFF_REASON_LABELS[row.alert]?.[row.action.reason_code];
          return (
            <div className="flex flex-col items-start gap-0.5">
              <span className={ACTIONED_STYLE} title={recorded}>
                <span className="line-clamp-2">{recorded}</span>
              </span>
              {row.action.reason_label && (
                <span className={ACTIONED_STYLE} title={row.action.reason_label}>
                  <span>{row.action?.action_note}</span>
                </span>
              )}
            </div>
          );
        }
        return <ActionButton label="Take Action" tone="danger" onClick={() => setAllStaffActionRow(row)} />;
      },
    },
  ];

  const groomerLineItem = useMemo(() => (groomerSummary ? [groomerSummary] : []), [groomerSummary]);

  const renderBody = (
    isLoading: boolean,
    hasError: boolean,
    isEmpty: boolean,
    emptyMessage: string,
    emptyDescription: string,
    content: React.ReactNode,
  ) => {
    if (isLoading) return <SkeletonType1 />;
    if (hasError)
      return (
        <EmptyNote message="Attendance did not load" description="The request failed. Refresh the page to try again." />
      );
    if (isEmpty) return <EmptyNote message={emptyMessage} description={emptyDescription} />;
    return content;
  };

  if (!mappedClinicId) {
    return (
      <div className="rounded-lg bg-white px-5 py-4">
        <EmptyNote
          message="No clinic mapped"
          description="Map your designated clinic to see today's staff attendance."
        />
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white px-5 py-4">
      <Section
        heading="Groomer Attendance"
        description="Groomers at this clinic, counted together against the day's grooming ROTA."
      >
        {renderBody(
          isLoading,
          errors.groomers || !groomerSummary,
          groomerSummary?.rota_staff === 0 && groomerSummary?.attended_staff === 0,
          "No grooming activity at this clinic",
          "No grooming seat was rostered and no mapped groomer punched on this date.",
          <>
            {groomerSummary?.no_out_time_count > 0 && (
              <p className="mb-2 text-[11px] leading-4 text-amber-700">
                {groomerSummary.no_out_time_count} of {groomerSummary.attended_staff} have not punched out yet —
                attended time is incomplete.
              </p>
            )}

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <DataTable data={groomerLineItem} columns={groomerColumns} maxHeight="240px" />
            </div>
          </>,
        )}
      </Section>

      <Section
        heading="Doctor Attendance"
        description="Every doctor with a ROTA or attendance at this clinic for the day."
      >
        {renderBody(
          isLoading,
          errors.doctors,
          doctors.length === 0,
          "No doctors for today",
          "Nobody is rostered at this clinic and no punch was recorded here today.",
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <DataTable data={doctors} columns={doctorColumns} maxHeight="400px" />
          </div>,
        )}
      </Section>

      <Section
        heading="All Staff Attendance"
        description="Everyone mapped to or attending this clinic, against a 10 hour shift."
      >
        {renderBody(
          isLoading,
          errors.allStaff,
          allStaff.length === 0,
          "No staff for today",
          "Nobody is mapped to this clinic and no punch was recorded here today.",
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <DataTable data={allStaff} columns={allStaffColumns} maxHeight="400px" />
          </div>,
        )}
      </Section>

      <AlertActionModal
        row={doctorActionRow}
        section="doctors"
        clinicId={mappedClinicId}
        date={date}
        onClose={() => setDoctorActionRow(null)}
        onSaved={() => fetchAttendance(mappedClinicId, date)}
      />

      <AlertActionModal
        row={
          allStaffActionRow
            ? {
                staff_id: allStaffActionRow.employee_code,
                name: allStaffActionRow.name,
                alert: allStaffActionRow.alert,
                action: allStaffActionRow.action,
              }
            : null
        }
        section="all_staff"
        clinicId={mappedClinicId}
        date={date}
        onClose={() => setAllStaffActionRow(null)}
        onSaved={() => fetchAttendance(mappedClinicId, date)}
      />

      {groomerSummary && (
        <AlertActionModal
          row={
            groomerActionAlert
              ? {
                  name: "Groomers",
                  alert: groomerActionAlert,
                  action: groomerSummary.actions[groomerActionAlert],
                }
              : null
          }
          section="groomers"
          clinicId={mappedClinicId}
          date={date}
          onClose={() => setGroomerActionAlert(null)}
          onSaved={() => fetchAttendance(mappedClinicId, date)}
        />
      )}
    </div>
  );
};

export default StaffAttendanceClosingComponent;
